import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Paper, Stack, Tooltip, Typography,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../../animations/variants';
import {
  getIpamConfig, listIpamMembers, listIpamPublicAddresses, startIpamRefresh,
} from '../../../../services/ipamApi';
import PortalPageHeader from '../../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalErrorState, PortalSectionHeader,
} from '../../../../components/portal/PortalStates';
import { formatStatusLabel } from '../../../../utils/statusLabel';
import {
  CK, FONT_BODY, sectionCardSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  cidrCellSx, statusChipSx,
} from '../../portalStyles';
import IpamDiscoveryColdStart, { IPAM_DISCOVERY_POLL_MS } from './IpamDiscoveryColdStart';
// IMPORTED, NEVER RETYPED — the Accounts page states the same four reasons about
// its own refresh control, and two hand-typed copies of a reason are two reasons.
import {
  IPAM_REFRESH_NOT_CONFIGURED_REASON, IPAM_REFRESH_PENDING_REASON,
  IPAM_REFRESH_RUNNING_REASON, IPAM_REFRESH_UNREADABLE_REASON, ipamRefreshNotLiveReason,
  IPAM_LIVENESS_UNREAD_BODY, IPAM_LIVENESS_UNREAD_TITLE,
  ipamConfigNoteSeverity, ipamConfigNoteTitle, ipamPublicIpsZeroState, ipamResidualIpamAlert,
} from './ipamCopy';

// Public IP Insights — the idle-EIP / public-IPv4-cost inventory.
//
// ── Why this page is not a CIDR table with different columns ──────────────────
// Its row shape shares NOTHING with a discovered-CIDR row (spec §4.5d), and its
// backing AWS operation, GetIpamDiscoveredPublicAddresses, has NO SDK PAGINATOR
// in the pinned ec2 module. Prism hand-rolls the walk, so this page must never
// assume the first response is the whole answer: it ships a real cursor-driven
// "Load more" and says on screen that a filtered or sorted view describes only
// the addresses loaded so far.
//
// ── The money column ─────────────────────────────────────────────────────────
// `AssociationStatus: "disassociated"` was observed live on an unattached EIP
// [SPIKE-VERIFIED, round 3]. That is the idle-public-IPv4 bill, so it is what the
// default sort surfaces first — before the address order, before the account.
//
// ── The columns that were NEVER observed populated ───────────────────────────
// Service, ServiceResource, VpcId, SubnetId, NetworkInterfaceId,
// NetworkInterfaceDescription, InstanceId and SecurityGroups were ALL ABSENT from
// the one real page AWS returned, because the address was unattached — which is
// precisely the row this product exists to surface. Every one of them renders
// through <NotReported/>, never as an empty cell: a blank security-groups cell
// reads as "this address has no security groups", and what is actually true is
// "AWS did not tell us". Those are opposite statements.

const IPAM_COLOR = '#455A64';

const publicIpsHeaderIcon = <PublicOutlinedIcon sx={{ fontSize: 24 }} />;

// --- Is there an IPAM behind this config row? --------------------------------
//
// A CONFIG ROW IS NOT AN IPAM. One exists from the moment BeginIpamConfig runs —
// writeDraftConfig creates it at `awaiting_consent` — and a `failed` row persists
// forever afterwards. The sidebar renders all seven IPAM links whether or not the
// capability was ever enabled, so this page is directly reachable in every one of
// those states, and `status !== 'disabled'` was never a wide enough exclusion.
//
// `enabled` and `partial_failure` are the ONLY two statuses at which an IPAM
// exists and AWS's sweep is the thing being waited on. They are also exactly the
// pair StartRefresh accepts (services/ipam_service.go: "cannot refresh while VPC
// IPAM is %s") — which matters twice on this page, because BOTH buttons here
// dispatch a refresh.
const ipamIsLive = (status) => status === 'enabled' || status === 'partial_failure';

// The copy for a config row with NO live IPAM behind it and no sweep ever
// completed. Every one of these states used to render the cold-start panel —
// "AWS is still taking its first inventory… Nothing is wrong and there is nothing
// to retry" — under an indeterminate bar that will never finish, above a "Check
// for results" button whose refresh the backend refuses because the config is not
// settled. There is no IPAM, no discovery and no association: nothing will ever
// arrive, and each state has a different way out, so each one says its own.
const NOT_LIVE_COPY = {
  awaiting_consent: 'Amazon VPC IPAM setup was started but stopped at the cost estimate, so no IPAM '
    + 'and no resource discovery were ever created. Public IP insights are built from IPAM’s own '
    + 'discovery sweep, and no sweep is coming — finish the setup wizard first.',
  in_progress: 'Amazon VPC IPAM is still being set up and AWS has not completed a discovery sweep '
    + 'yet. Public IP insights are built from that sweep, so there is nothing to show until it '
    + 'lands.',
  failed: 'Amazon VPC IPAM setup failed, so there is no IPAM and no discovery sweep behind these '
    + 'numbers. Nothing is running and nothing will resume on its own — re-run the setup wizard.',
  blocked: 'A VPC IPAM teardown stopped part-way because member accounts still hold live '
    + 'allocations, and this IPAM never completed a discovery sweep, so there are no addresses to '
    + 'show. The IPAM is still there and still billing until it is deleted.',
  disabled: 'Amazon VPC IPAM is not set up yet for this customer. Public IP insights are built from '
    + 'IPAM’s own discovery sweep, so there is nothing to show until the capability is enabled.',
};

const notLiveCopy = (status) => NOT_LIVE_COPY[status] || NOT_LIVE_COPY.disabled;

// Is there anything for this page to draw at all?
//
// `in_progress` and `blocked` are the two statuses that are not live and can
// still have a real IPAM behind them — an operation running over a working one,
// and a teardown parked on one that is still there and STILL BILLING for every
// public IPv4 address below — so those two are judged on whether a sweep has ever
// landed, and fall through to the table when it has.
//
// EVERY OTHER NON-LIVE STATUS IS JUDGED ON THE STATUS ALONE, deliberately
// ignoring firstDiscoveryAt. That timestamp "IS SET ONCE AND NEVER RESET"
// (services/ipam_service.go, writeRefreshFreshness) and BeginIpamConfig upserts
// the existing row, so a tenant who tore an IPAM down and started the wizard
// again sits at `awaiting_consent` still carrying the old one. Reading it as
// "this tenant has discovered before" would put stale addresses under a
// live-looking table — a claim about a bill, made out of a dead config.
const ipamHasNothingToShow = (config) => {
  if (!config) return true;
  if (ipamIsLive(config.status)) return false;
  if (config.status === 'in_progress' || config.status === 'blocked') {
    return !config.firstDiscoveryAt;
  }
  return true;
};

// THE THIRD ANSWER, PROPAGATED FROM IpamPools.js's ipamLiveness. A config row
// carrying NO STATUS AT ALL is not a decided negative — it is a field Prism never
// read — and without this arm NOT_LIVE_COPY's `disabled` fallback dresses the
// blank as "Amazon VPC IPAM is not set up yet for this customer", which is a
// verdict invented out of a field nobody read.
const ipamStatusUnread = (config) => !!config && !config.status;

// --- The Refresh gate: live / not live / UNKNOWN -----------------------------
//
// `disabled={checking || !ipamIsLive(config?.status)}` was two-valued over a
// three-valued fact, and the arm it had no room for is the one that hurts. See
// IPAM_REFRESH_UNREADABLE_REASON in ipamCopy.js for the full argument; the short
// version is that a failed GET /config left this button dead, silent and still
// labelled "Refresh", which is an absence rendered as a verdict.
//
// The FAILED-READ ARM DELIBERATELY LEAVES THE BUTTON ENABLED rather than
// disabling it beside a "retry the config read" link. Three reasons, all local
// to this page:
//
//   1. The authority already lives on the other side of the click. StartRefresh
//      enforces exactly `enabled || partial_failure` ("cannot refresh while VPC
//      IPAM is %s"), so a refusal arrives as a real sentence in the error Alert
//      instead of a guess made in the browser.
//   2. `onRefresh` runs loadContext() on its way through, so the click IS the
//      retry for the read that failed — one honest control instead of two.
//   3. The page already offers a dedicated retry in the only branch this state
//      can reach: the failed-read guard below renders PortalErrorState with
//      onRetry. A second retry affordance in the header would be the third
//      control for one job.
//
// `pending` is disabled instead, because it resolves itself in a moment and
// asking the operator to act during it is noise — but it still says so, since a
// button that is off for a second and a button that is off forever are
// indistinguishable when neither explains itself.
//
// Note the arms are ordered so that a config row Prism actually holds always
// wins: a background read that fails later must not downgrade a status already
// read successfully to "unknown".
const refreshGate = (config, configRead, stateLabel) => {
  if (config) {
    if (ipamIsLive(config.status)) {
      return {
        disabled: false,
        reason: 'Re-read the discovered public IPv4 addresses from AWS.',
      };
    }
    // A config row carrying no status at all is not a decided "not live"
    // either — same rule, one level down. Do not invent a refusal reason for a
    // blank; treat it as unread.
    if (!stateLabel) return { disabled: false, reason: IPAM_REFRESH_UNREADABLE_REASON };
    return { disabled: true, reason: ipamRefreshNotLiveReason(stateLabel) };
  }
  // No config row. On a SUCCESSFUL read that is the route's documented answer for
  // a tenant that never configured IPAM (200 + data:null), which is a real
  // negative. On anything else it is an unknown, and must not borrow that answer.
  if (configRead === 'ok') return { disabled: true, reason: IPAM_REFRESH_NOT_CONFIGURED_REASON };
  if (configRead === 'pending') return { disabled: true, reason: IPAM_REFRESH_PENDING_REASON };
  return { disabled: false, reason: IPAM_REFRESH_UNREADABLE_REASON };
};

// --- The one member filter ---------------------------------------------------
//
// SHARED WITH IpamDashboard.js AND SPELLED IDENTICALLY ON ALL FOUR PAGES.
// `disabled` is a tombstone and `removing` is a departure already in flight;
// neither is an account whose coverage could be missing, so neither belongs in
// any denominator here. Four pages used to spell this four different ways and
// disagreed on screen about the same accounts.
const isLiveMember = (m) => m?.phase !== 'disabled' && m?.phase !== 'removing';

// AWS's AssociationStatus. Two documented values, and the third arm is not
// defensive padding: an address whose status AWS did not report must not be
// drawn as "in use" (which hides a bill) or as "idle" (which invents one).
const ASSOCIATION_META = {
  disassociated: {
    intent: 'warning',
    label: 'Idle',
    hint: 'AWS reports this address is attached to nothing. An allocated public IPv4 address bills '
      + 'for every hour it exists whether or not it is attached, so this is a live charge with no '
      + 'workload behind it. Release it in the owning account to stop it — Prism never releases an '
      + 'address; this page is an inventory.',
  },
  associated: {
    intent: 'success',
    label: 'In use',
    hint: 'AWS reports this address is attached to a resource.',
  },
};

const UNKNOWN_ASSOCIATION = {
  intent: 'neutral',
  label: 'Not reported',
  hint: 'AWS did not report an association status for this address. That is not the same as the '
    + 'address being in use — it is unmeasured, and it may still be idle and billing.',
};

const associationMeta = (v) => ASSOCIATION_META[String(v || '').toLowerCase()]
  || (v ? { ...UNKNOWN_ASSOCIATION, label: formatStatusLabel(v) } : UNKNOWN_ASSOCIATION);

// AddressType tells BYOIP from an Amazon-owned address. PublicIpv4PoolId does
// NOT: it came back "amazon" for a non-BYOIP address [SPIKE-VERIFIED, round 3],
// so anything reading that column as a BYOIP marker is wrong.
const ADDRESS_TYPE_META = {
  'amazon-owned-eip': { intent: 'info', label: 'Amazon-owned EIP' },
  'amazon-owned-contig': { intent: 'info', label: 'Amazon-owned (contiguous)' },
  'byoip': { intent: 'purple', label: 'BYOIP' },
  'ec2-public-ip': { intent: 'neutral', label: 'EC2 public IP' },
  'service-managed-ip': { intent: 'neutral', label: 'Service-managed IP' },
  'service-managed-byoip': { intent: 'purple', label: 'Service-managed BYOIP' },
};

const addressTypeMeta = (v) => ADDRESS_TYPE_META[String(v || '').toLowerCase()]
  || { intent: 'neutral', label: formatStatusLabel(v) || 'Unknown' };

// --- Response reader --------------------------------------------------------
//
// /inventory/public-addresses answers ONE envelope, fixed by
// services.IpamInventoryPage and asserted by the backend's own tests:
//
//   {"items": [...], "nextCursor": ""}
//
// `items` is NEVER null on a successful read — the un-enabled tenant and the
// cold-start tenant both answer `{"items": [], "nextCursor": ""}` — and
// `nextCursor` is always present, "" meaning the walk is exhausted.
//
// This reader used to accept a bare array, then a list of plausible key names,
// then ANY array-valued property. That tolerance was right while the route
// answered 501 and no DTO pinned the wrapper. It is wrong now, and on this page
// it is the most expensive kind of wrong: a reader that accepts five shapes
// cannot distinguish a renamed key from a normal response, so a shape change
// renders an empty table — and "no public IPv4 addresses" here is a claim about
// a customer's bill. A broken envelope is now raised, not absorbed.
const ENVELOPE_ERROR = 'Prism could not read the public IPv4 inventory: the service answered in a '
  + 'shape this page does not recognise. This is a defect, not an empty result — no claim is being '
  + 'made about what you are paying for until the feed can be read.';

const inventoryPage = (data) => {
  if (!data || typeof data !== 'object'
    || !Array.isArray(data.items) || typeof data.nextCursor !== 'string') {
    throw new Error(ENVELOPE_ERROR);
  }
  return data;
};

// OperatingRegions is a JSON []string on the config row. Tolerate a decoded array
// so the scope sentence never blanks if the DTO shape shifts.
const toStringArray = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

// Tags arrive FLATTENED to the inner EipTags array — AWS returns
// {"EipTags":[{Key,Value}]} and the flattening happens at ingest, never here. A
// row cached before that landed could still carry the nested object, so both
// shapes are unwrapped rather than one of them silently rendering "0 tags".
const parseTags = (v) => {
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object') return Array.isArray(v.EipTags) ? v.EipTags : [];
  try {
    const parsed = JSON.parse(v || '[]');
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.EipTags)) return parsed.EipTags;
    return [];
  } catch (_) { return []; }
};

// SecurityGroups is documented as a JSON []string, and its populated shape is
// [UNVERIFIED] — no attached address was ever observed. Objects are unwrapped as
// well as strings so an id-plus-name shape does not render as "[object Object]".
const parseGroups = (v) => {
  const raw = Array.isArray(v) ? v : (() => {
    try { const p = JSON.parse(v || '[]'); return Array.isArray(p) ? p : []; } catch (_) { return []; }
  })();
  return raw
    .map((g) => (typeof g === 'string' ? g : (g?.GroupId || g?.groupId || g?.GroupName || g?.groupName || '')))
    .filter(Boolean);
};

const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

const absoluteTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
};

const plural = (n, one, many) => (n === 1 ? one : many);

// The single rule that keeps an unmeasured field from reading as a measured
// absence. Every column AWS did not populate on the observed page goes through
// here, and each one carries WHY it might be missing rather than a generic
// shrug — "no security groups" and "we were not told about security groups" lead
// an operator to opposite conclusions.
function NotReported({ hint }) {
  return (
    <Tooltip title={hint}>
      <Box
        component="span"
        sx={{
          fontFamily: FONT_BODY,
          color: CK.disabled,
          cursor: 'help',
          borderBottom: `1px dotted ${CK.disabled}`,
        }}
      >
        —
      </Box>
    </Tooltip>
  );
}

const ATTACHMENT_HINT = 'AWS reported no attachment details for this address. On the one real page '
  + 'AWS returned, every attachment field was absent because the address was unattached — so an '
  + 'empty value here means "not reported", not "not attached".';

const GROUPS_HINT = 'AWS reported no security groups for this address. That is NOT the same as the '
  + 'address having none: the field is absent on an unattached address, and its populated shape has '
  + 'never been observed. Treat it as unknown.';

// A cell that renders a value when there is one and the honest em-dash when
// there is not. `mono` is for anything scanned character-by-character — an
// address, an eipalloc-/vpc-/eni- id, a 12-digit account.
function ValueCell({ value, hint, mono = false, align = 'left' }) {
  const text = value === null || value === undefined ? '' : String(value).trim();
  return (
    <TableCell align={align} sx={mono ? cidrCellSx : bodyCellSx}>
      {text ? text : <NotReported hint={hint} />}
    </TableCell>
  );
}

export default function IpamPublicIPs() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);

  // THE OUTCOME OF THE CONFIG READ, AS ITS OWN VALUE — 'pending' | 'ok' | 'failed'.
  //
  // `config === null` cannot carry this. It is the same null for "the read has
  // not returned", "the read rejected" and "the read returned the route's real
  // answer of no configuration", and every control that branched on it therefore
  // spoke the third meaning in all three cases. `error` cannot carry it either:
  // it is shared with onRefresh/onCheck, so a rejected POST would read as a
  // rejected config read. The third case gets a slot in the state instead of
  // being inferred from the absence of the other two.
  const [configRead, setConfigRead] = useState('pending');

  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState('');
  const [tableError, setTableError] = useState(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [checking, setChecking] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);

  // Filters are CLIENT-SIDE, over the pages already fetched. No server-side
  // filter struct exists for the inventory reads, and a query key the backend
  // ignores is a filter that silently does nothing — worse than no filter at all
  // on a page whose whole job is telling an operator what they are paying for.
  const [statusFilter, setStatusFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [search, setSearch] = useState('');

  const reqSeq = useRef(0);
  // A SECOND monotonic guard, for the address feed alone. Filtering here is
  // client-side so there is no keystroke storm to debounce, but "Refresh", the
  // cold-start poll and "Load more" can all be in flight at once — and without an
  // ordering guard an earlier response resolving last replaces the newer page,
  // which on this table means showing an operator a stale set of billing rows.
  const addrSeq = useRef(0);
  const pollRef = useRef(null);
  const pollFailuresRef = useRef(0);

  const loadContext = useCallback(async (showSpinner = true) => {
    const myReq = ++reqSeq.current;
    if (showSpinner) setLoading(true);
    try {
      const [c, m] = await Promise.all([getIpamConfig(), listIpamMembers()]);
      if (myReq !== reqSeq.current) return;
      setConfig(c?.data || null);
      setMembers(m?.data || []);
      setConfigRead('ok');
      setError(null);
    } catch (e) {
      if (myReq !== reqSeq.current) return;
      // Conservative on purpose: the two reads share one try, so a members
      // failure also marks the config unread. Erring towards "we do not know"
      // costs an offered click the service will answer; erring the other way is
      // the dead button this whole change exists to remove.
      setConfigRead('failed');
      setError(e.message);
    } finally {
      if (myReq === reqSeq.current && showSpinner) setLoading(false);
    }
  }, []);

  // The address page is loaded SEPARATELY from config+members rather than in one
  // Promise.all: a failure of this one read must not blank a page that can still
  // truthfully render the cold-start or the broken-association banner.
  const loadAddresses = useCallback(async (cursor = null) => {
    const append = !!cursor;
    const myReq = ++addrSeq.current;
    if (append) setLoadingMore(true); else setTableLoading(true);
    try {
      const res = await listIpamPublicAddresses(cursor ? { cursor } : undefined);
      if (myReq !== addrSeq.current) return;
      const page = inventoryPage(res?.data);
      setRows((prev) => (append ? [...prev, ...page.items] : page.items));
      setNextCursor(page.nextCursor);
      setTableError(null);
    } catch (e) {
      if (myReq !== addrSeq.current) return;
      setTableError(e.message);
      if (!append) { setRows([]); setNextCursor(''); }
    } finally {
      if (myReq === addrSeq.current) {
        if (append) setLoadingMore(false); else setTableLoading(false);
      }
    }
  }, []);

  useEffect(() => { loadContext(); loadAddresses(); }, [loadContext, loadAddresses]);

  // Cold start needs BOTH halves. `firstDiscoveryAt == null` alone means "no
  // sweep has ever landed", which is equally true of a tenant waiting on AWS and
  // of a tenant parked at the cost gate or sitting on a failed setup — and only
  // the first of those is a cold start. Excluding `disabled` was not enough,
  // because a draft or failed config is neither disabled nor live.
  const coldStart = !!config && ipamIsLive(config.status) && !config.firstDiscoveryAt;

  // 30 s, not the shared 3 s POLL_INTERVAL_MS: the first AWS sweep takes up to
  // about 20 minutes, and three-second polling against it is ~400 pointless
  // requests per tenant per cold start. Owned by a ref, keyed on primitives, and
  // three consecutive failures stop it rather than hammering a broken backend
  // until the tab is closed.
  useEffect(() => {
    if (!coldStart) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollFailuresRef.current = 0;
    pollRef.current = setInterval(async () => {
      try {
        const [c, m] = await Promise.all([getIpamConfig(), listIpamMembers()]);
        pollFailuresRef.current = 0;
        setConfig(c?.data || null);
        setMembers(m?.data || []);
        if (c?.data?.firstDiscoveryAt) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          loadAddresses();
        }
      } catch (_) {
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= 3 && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    }, IPAM_DISCOVERY_POLL_MS);
  }, [coldStart, loadAddresses]);

  useEffect(() => () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  // Live enrollment cells: a tombstoned row is not an account in scope, and
  // counting one would overstate the denominator in every sentence below.
  //
  // `removing` joins `disabled`, so this is now the SAME FILTER IpamDashboard.js
  // applies — an account mid-removal is a departure in flight, not a coverage
  // gap. Matching matters beyond tidiness: removeMember tombstones by writing
  // {"phase", "error_message"} and nothing else, and syncIpamLifecycleStates
  // skips `phase = disabled` cells, so an account removed BECAUSE its discovery
  // was not-found keeps `resource_discovery_status = 'not-found'` forever. A
  // looser filter here means the warning banner below names an account that is
  // no longer enrolled, permanently.
  const liveMembers = useMemo(() => members.filter(isLiveMember), [members]);

  // The cold-start component's chips are the load-bearing part of it: they are
  // what separates "AWS has not swept yet" from "the RAM share was revoked and
  // we will wait forever".
  //
  // THE HOST IS DROPPED, for the reason IpamDashboard.js:464-467 gives. The host
  // cell never carries a resource_discovery_status — writeDraftConfig creates it
  // without one and the enrollment fan-out excludes it explicitly
  // (`account_id <> hostAccountID`) — so discoveryChipIntent('') falls through to
  // 'warning' and the host renders as an amber chip labelled "123456789012 · ",
  // trailing separator and all, among green member chips. A permanently unknown
  // chip is a standing false alarm in the one component whose whole job is making
  // a real alarm visible. Address Space and Allocations already drop it; this
  // matches them, and takes their homeRegion fallback with it so a member whose
  // discovery Region has not been recorded yet cannot render that separator
  // either.
  const associations = useMemo(
    () => liveMembers
      .filter((m) => m.role !== 'host')
      .map((m) => ({
        accountId: m.accountId,
        region: m.discoveryRegion || config?.homeRegion || '',
        status: m.resourceDiscoveryStatus,
      })),
    [liveMembers, config],
  );

  const brokenAssociations = useMemo(
    () => liveMembers.filter((m) => m.resourceDiscoveryStatus === 'not-found'),
    [liveMembers],
  );

  const operatingRegions = useMemo(
    () => toStringArray(config?.operatingRegions),
    [config?.operatingRegions],
  );

  const regionOptions = useMemo(() => {
    const seen = new Set(rows.map((r) => r.addressRegion).filter(Boolean));
    return [...seen].sort((a, b) => String(a).localeCompare(String(b)));
  }, [rows]);

  // Default order: idle first, then unreported, then in use. That IS the product
  // — the disassociated rows are the bill — and the address sort key only breaks
  // ties inside a bucket.
  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rank = (r) => {
      const s = String(r.associationStatus || '').toLowerCase();
      if (s === 'disassociated') return 0;
      if (s === 'associated') return 2;
      return 1;
    };
    return rows
      .filter((r) => {
        if (statusFilter && String(r.associationStatus || '').toLowerCase() !== statusFilter) return false;
        if (regionFilter && r.addressRegion !== regionFilter) return false;
        if (!needle) return true;
        return [
          r.address, r.addressAllocationId, r.addressOwnerId, r.vpcId, r.instanceId,
          r.networkInterfaceId, r.service, r.serviceResource, r.publicIpv4PoolId,
        ].some((v) => String(v || '').toLowerCase().includes(needle));
      })
      .sort((a, b) => {
        const d = rank(a) - rank(b);
        if (d !== 0) return d;
        const ka = String(a.addressSortKey || a.address || '');
        const kb = String(b.addressSortKey || b.address || '');
        if (ka !== kb) return ka.localeCompare(kb);
        return String(a.addressRegion || '').localeCompare(String(b.addressRegion || ''));
      });
  }, [rows, statusFilter, regionFilter, search]);

  const idleCount = useMemo(
    () => rows.filter((r) => String(r.associationStatus || '').toLowerCase() === 'disassociated').length,
    [rows],
  );

  const filtered = !!(statusFilter || regionFilter || search.trim());

  const onCheck = async () => {
    setChecking(true);
    try {
      // 'discovery' rather than 'public_addresses': in the cold state AWS has not
      // completed a sweep at all, so re-reading the public-address feed would
      // re-read nothing. The sweep is what the operator is waiting for.
      await startIpamRefresh('discovery');
      setLastCheckedAt(new Date().toISOString());
      await loadContext(false);
      await loadAddresses();
    } catch (e) {
      setError(e.message);
    } finally {
      setChecking(false);
    }
  };

  const onRefresh = async () => {
    setChecking(true);
    try {
      await startIpamRefresh('public_addresses');
      await loadContext(false);
      await loadAddresses();
    } catch (e) {
      setError(e.message);
    } finally {
      setChecking(false);
    }
  };

  // Judged against the SAME predicate StartRefresh enforces wherever Prism has
  // actually read a status, so the button is not offered where the backend would
  // answer "cannot refresh while VPC IPAM is <status>" — and left alone where
  // Prism has NOT read one, because that is not the same fact. See refreshGate.
  //
  // This matters in more than the error branch: `in_progress` and `blocked` with
  // a sweep already landed fall through to the full table below, so a not-live
  // Refresh is disabled on a page that renders no not-live banner at all. Before
  // this, that state was a dead button with nothing on screen explaining it.
  const gate = refreshGate(config, configRead, formatStatusLabel(config?.status));

  const header = (
    <PortalPageHeader
      icon={publicIpsHeaderIcon}
      title="Public IP insights"
      subtitle="Every public IPv4 address IPAM discovered across the accounts in scope — and which of them are attached to nothing"
      color={IPAM_COLOR}
      action={(
        <Tooltip title={checking ? IPAM_REFRESH_RUNNING_REASON : gate.reason}>
          {/* A disabled MUI Button emits no pointer events, so a Tooltip on it
              never fires — the reason would exist in the source and nowhere on
              the screen, which is the defect wearing a fix. The <span> is what
              IpamDashboard.js already wraps its Refresh in. */}
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              disabled={checking || gate.disabled}
              onClick={onRefresh}
              sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
            >
              {checking ? 'Refreshing…' : 'Refresh'}
            </Button>
          </span>
        </Tooltip>
      )}
    />
  );

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading public IP insights…" color={IPAM_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // MUST precede the not-configured branch: a rejection from GET /config leaves
  // `config` null, and without this a 500 renders as "IPAM is not set up yet" and
  // sends an operator whose IPAM is running and billing to the setup wizard.
  //
  // Keyed on the READ OUTCOME as well as on `error`, not on `error` alone. The
  // old guard held only while every rejection path happens to produce a truthy
  // message, and it is the not-configured branch below — a claim about a
  // customer's setup — that inherits the case where one does not.
  if ((error || configRead === 'failed') && !config) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => { loadContext(); loadAddresses(); }} />
        </motion.div>
      </motion.div>
    );
  }

  // NOT just `status === 'disabled'`. A draft parked at the cost gate and a
  // failed setup both have a config row and no IPAM behind it, and both used to
  // fall past this branch into the cold-start panel.
  if (ipamHasNothingToShow(config)) {
    // THREE ANSWERS, NOT ONE SENTENCE.
    //
    //   - A BLANK STATUS is unread, and must not be dressed as "not set up".
    //   - A RESIDUAL IPAM — an adopted one a clean teardown left running, or one
    //     CreateIpam made before a later host step failed — is an IPAM that EXISTS
    //     and is metering, and "Amazon VPC IPAM is not set up yet for this
    //     customer" is then the one false sentence on the screen. See
    //     ipamResidualIpamCard for why ipamId alone cannot decide this.
    //   - Otherwise the status is the whole answer, and NOT_LIVE_COPY stands.
    const residual = ipamResidualIpamAlert(config, 'public IP addresses');
    const unread = ipamStatusUnread(config);
    const body = unread
      ? `${IPAM_LIVENESS_UNREAD_TITLE}. ${IPAM_LIVENESS_UNREAD_BODY}`
      : (residual || notLiveCopy(config?.status));
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert
            severity={residual && !unread ? 'error' : 'info'}
            action={(
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/ipam')}>
                Go to setup
              </Button>
            )}
          >
            {body}
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  const scopeSentence = `the ${liveMembers.length} ${plural(liveMembers.length, 'account', 'accounts')} × `
    + `${operatingRegions.length} ${plural(operatingRegions.length, 'region', 'regions')} currently in scope`;
  const asOf = absoluteTime(config.lastDiscoveryRefreshAt);

  // THE ZERO STATE IS NOT DECIDED HERE, and the reason is in ipamCopy.js: the
  // sentence it may print is a claim about what AWS reported, and the ONLY thing
  // that licenses it is config.publicAddressesReadAt — the server's record of a
  // refresh in which every public-address read completed. `firstDiscoveryAt`
  // cannot: it is one aggregate over five caches, so a tenant with discovered
  // CIDRs and an unread address cache disarms the cold-start panel and lands
  // here, which is exactly what happened live on 2026-08-23.
  //
  // NOT a useMemo — this is below the page's early returns, so a hook here would
  // run conditionally.
  const noteSeverity = ipamConfigNoteSeverity(config.note, config.noteSource);
  const noteTitle = ipamConfigNoteTitle(config.note, config.noteSource);

  const zeroState = ipamPublicIpsZeroState({
    cacheReadAt: config.publicAddressesReadAt,
    morePagesRemain: !!nextCursor,
    brokenCount: brokenAssociations.length,
    discoveringCount: liveMembers.length - brokenAssociations.length,
    scopeSentence,
    asOf,
  });

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{header}</motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        </motion.div>
      )}

      {/* THE DEGRADATION NOTE, ON THE PAGE THE STALE ROWS ARE ON. `config.note`
          carries the unattributed degradation channel — "AWS no longer reports
          IPAM %s … Nothing is being discovered" — which rendered as
          severity="info" on the dashboard and on no other page. */}
      {noteSeverity && (
        <motion.div variants={sectionVariants}>
          <Alert severity={noteSeverity} sx={{ mb: 3 }}>
            {noteTitle && (
              <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>{noteTitle}</AlertTitle>
            )}
            {config.note}
          </Alert>
        </motion.div>
      )}

      {/* STATE C — silent breakage, ALWAYS drawn as a warning ABOVE the results.
          An account whose discovery was deleted or whose RAM share was revoked
          contributes nothing to the table below, however clean the table looks,
          and no amount of waiting changes that. */}
      {brokenAssociations.length > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              {brokenAssociations.length} of {liveMembers.length}{' '}
              {plural(liveMembers.length, 'account is', 'accounts are')} no longer being discovered.
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', mb: 1 }}>
              Their resource discovery was deleted or their RAM share was revoked, so any public IPv4
              address in those accounts is missing from this table however clean it looks — including
              idle addresses that are still billing.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
              {brokenAssociations.slice(0, 12).map((m) => (
                <Chip
                  key={`${m.accountId}:${m.discoveryRegion}`}
                  size="small"
                  label={`${m.accountId} · ${m.discoveryRegion}`}
                  sx={statusChipSx('error')}
                />
              ))}
              {brokenAssociations.length > 12 && (
                <Chip size="small" label={`+${brokenAssociations.length - 12} more`} sx={statusChipSx('neutral')} />
              )}
            </Stack>
            <Button
              size="small"
              color="inherit"
              onClick={() => navigate('/securityhub/ipam/accounts')}
              sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
            >
              Review on the Accounts page
            </Button>
          </Alert>
        </motion.div>
      )}

      {/* STATE A — cold start, discriminated by the SERVER-side
          config.firstDiscoveryAt, never by a client timer. A client heuristic is
          wrong across a reload, wrong for a tab left open overnight, and wrong for
          an account added six months later. */}
      {coldStart ? (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            {/* IpamConfig carries no `enabledAt`. The four pages that share this
                component all spell the fallback the same way — a caption reading
                "Enabled 4 min ago" here and "2 h ago" on the Dashboard is the kind
                of disagreement that makes an operator distrust every number on
                the screen. */}
            <IpamDiscoveryColdStart
              associations={associations}
              enabledAt={config?.costAcknowledgedAt || config?.createdAt}
              lastCheckedAt={lastCheckedAt}
              lastSweepAt={config?.lastDiscoveryRefreshAt}
              onCheck={onCheck}
              checking={checking}
            />
          </Paper>
        </motion.div>
      ) : (
        <>
          <motion.div variants={sectionVariants}>
            <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
              <PortalSectionHeader
                icon={<PaidOutlinedIcon sx={{ fontSize: 20 }} />}
                title="Filters"
                subtitle="Applied in the browser, to the addresses loaded so far — not to the whole dataset"
                color={IPAM_COLOR}
              />
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                useFlexGap
                flexWrap="wrap"
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Association</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Association"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="disassociated">Idle (disassociated)</MenuItem>
                    <MenuItem value="associated">In use (associated)</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Region</InputLabel>
                  <Select
                    value={regionFilter}
                    label="Region"
                    onChange={(e) => setRegionFilter(e.target.value)}
                  >
                    <MenuItem value="">All regions</MenuItem>
                    {regionOptions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Search address, allocation, account, VPC…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flexGrow: 1, minWidth: 220 }}
                />
              </Stack>
            </Paper>
          </motion.div>

          <motion.div variants={sectionVariants}>
            <Paper elevation={0} sx={sectionCardSx}>
              <PortalSectionHeader
                icon={publicIpsHeaderIcon}
                title="Public IPv4 addresses"
                subtitle="Idle addresses are listed first — an allocated address bills whether or not anything is attached to it"
                color={IPAM_COLOR}
                action={(
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={`${idleCount} idle`}
                      sx={statusChipSx(idleCount > 0 ? 'warning' : 'neutral')}
                    />
                    <Chip
                      size="small"
                      label={`${rows.length}${nextCursor ? '+' : ''} loaded`}
                      sx={statusChipSx('info')}
                    />
                  </Stack>
                )}
              />

              {tableError && <PortalErrorState error={tableError} onRetry={() => loadAddresses()} sx={{ mb: 2 }} />}

              {tableLoading && rows.length === 0 ? (
                <PortalLoading label="Loading public IPv4 addresses…" color={IPAM_COLOR} />
              ) : (
                <>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 1500 }}>
                      <TableHead>
                        <TableRow sx={tableHeadRowSx}>
                          <TableCell sx={headCellSx}>Address</TableCell>
                          <TableCell sx={headCellSx}>Association</TableCell>
                          <TableCell sx={headCellSx}>Type</TableCell>
                          <TableCell sx={headCellSx}>Region</TableCell>
                          <TableCell sx={headCellSx}>Owner account</TableCell>
                          <TableCell sx={headCellSx}>Allocation</TableCell>
                          <TableCell sx={headCellSx}>Attached to</TableCell>
                          <TableCell sx={headCellSx}>VPC / subnet</TableCell>
                          <TableCell sx={headCellSx}>Network interface</TableCell>
                          <TableCell sx={headCellSx}>Security groups</TableCell>
                          <TableCell sx={headCellSx}>IPv4 pool</TableCell>
                          <TableCell sx={headCellSx}>Border group</TableCell>
                          <TableCell sx={headCellSx}>Tags</TableCell>
                          <TableCell sx={headCellSx}>Sampled</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleRows.map((r, index) => {
                          const assoc = associationMeta(r.associationStatus);
                          const type = addressTypeMeta(r.addressType);
                          const tags = parseTags(r.tags);
                          const groups = parseGroups(r.securityGroups);
                          const sampled = absoluteTime(r.sampleTime);
                          return (
                            <TableRow
                              key={r.id || `${r.address}|${r.addressRegion}`}
                              hover
                              sx={rowSx(index)}
                            >
                              <TableCell sx={cidrCellSx}>{r.address || '—'}</TableCell>
                              <TableCell sx={bodyCellSx}>
                                <Tooltip title={assoc.hint}>
                                  <Chip size="small" label={assoc.label} sx={statusChipSx(assoc.intent)} />
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                <Chip size="small" label={type.label} sx={statusChipSx(type.intent)} />
                              </TableCell>
                              <ValueCell value={r.addressRegion} hint={ATTACHMENT_HINT} />
                              <ValueCell value={r.addressOwnerId} hint={ATTACHMENT_HINT} mono />
                              <ValueCell value={r.addressAllocationId} hint="AWS reported no allocation id. An address with no eipalloc- id is not an Elastic IP — it may be an EC2-assigned public IP, which is released with its instance." mono />
                              {/* Service / ServiceResource / InstanceId: NEVER
                                  observed populated. */}
                              <TableCell sx={bodyCellSx}>
                                {(r.service || r.serviceResource || r.instanceId) ? (
                                  <Stack spacing={0.5} alignItems="flex-start">
                                    {r.service && (
                                      <Chip size="small" label={r.service} sx={statusChipSx('info')} />
                                    )}
                                    {(r.serviceResource || r.instanceId) && (
                                      <Typography
                                        sx={{
                                          fontFamily: FONT_BODY, fontSize: '0.75rem',
                                          color: CK.body, wordBreak: 'break-all',
                                        }}
                                      >
                                        {r.serviceResource || r.instanceId}
                                      </Typography>
                                    )}
                                  </Stack>
                                ) : <NotReported hint={ATTACHMENT_HINT} />}
                              </TableCell>
                              <TableCell sx={cidrCellSx}>
                                {(r.vpcId || r.subnetId) ? (
                                  <Stack spacing={0.25} alignItems="flex-start">
                                    {r.vpcId && <Box component="span">{r.vpcId}</Box>}
                                    {r.subnetId && <Box component="span">{r.subnetId}</Box>}
                                  </Stack>
                                ) : <NotReported hint={ATTACHMENT_HINT} />}
                              </TableCell>
                              <TableCell sx={cidrCellSx}>
                                {r.networkInterfaceId ? (
                                  <Tooltip title={r.networkInterfaceDescription || 'No interface description reported.'}>
                                    <Box component="span">{r.networkInterfaceId}</Box>
                                  </Tooltip>
                                ) : <NotReported hint={ATTACHMENT_HINT} />}
                              </TableCell>
                              {/* The cell that must never be blank: a blank here
                                  reads as "no security groups", and what is true
                                  is "AWS did not tell us". */}
                              <TableCell sx={bodyCellSx}>
                                {groups.length > 0 ? (
                                  <Tooltip title={groups.join(', ')}>
                                    <Chip
                                      size="small"
                                      label={`${groups.length} ${plural(groups.length, 'group', 'groups')}`}
                                      sx={statusChipSx('neutral')}
                                    />
                                  </Tooltip>
                                ) : <NotReported hint={GROUPS_HINT} />}
                              </TableCell>
                              <TableCell sx={cidrCellSx}>
                                {r.publicIpv4PoolId ? (
                                  <Tooltip title={'AWS returns "amazon" for an ordinary Amazon-provided address, so this column is NOT a BYOIP marker — the Type column is.'}>
                                    <Box component="span">{r.publicIpv4PoolId}</Box>
                                  </Tooltip>
                                ) : <NotReported hint={ATTACHMENT_HINT} />}
                              </TableCell>
                              <ValueCell value={r.networkBorderGroup} hint={ATTACHMENT_HINT} />
                              <TableCell sx={bodyCellSx}>
                                {tags.length > 0 ? (
                                  <Tooltip
                                    title={tags.map((t) => `${t.Key ?? t.key}=${t.Value ?? t.value}`).join(', ')}
                                  >
                                    <Chip
                                      size="small"
                                      icon={<LabelOutlinedIcon />}
                                      label={`${tags.length} ${plural(tags.length, 'tag', 'tags')}`}
                                      sx={statusChipSx('neutral')}
                                    />
                                  </Tooltip>
                                ) : <NotReported hint="AWS reported no tags on this address." />}
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                {sampled ? (
                                  <Tooltip title={`AWS sampled this address at ${sampled}. It is AWS's own stamp, not the time Prism cached the row.`}>
                                    <Box component="span" sx={{ fontFamily: FONT_BODY, cursor: 'help' }}>
                                      {relativeTime(r.sampleTime)}
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <NotReported hint="AWS returned no sample time for this address, so how fresh it is cannot be stated." />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}

                        {/* A FAILED READ IS NOT A ZERO. Both scoped empty states
                            below assert that AWS discovered no addresses in a named
                            scope, and neither is true when the feed did not answer —
                            including when it answered in a shape this page does not
                            recognise. Manufacturing that zero is the exact failure the
                            old tolerant reader hid, and on this page it would read as
                            "you are paying for nothing". The error card above stands
                            alone instead. */}
                        {visibleRows.length === 0 && tableError && (
                          <TableRow>
                            <TableCell colSpan={14} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
                                This table is empty because the read failed, not because AWS reported
                                no public IPv4 addresses. See the error above.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {visibleRows.length === 0 && !tableError && (
                          <TableRow>
                            <TableCell colSpan={14} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                              {/* STATE B — genuinely empty, and SCOPED. The bare
                                  words "no resources found" are never rendered:
                                  naming the accounts, the regions and the sweep
                                  time is what makes an honest zero
                                  distinguishable from a broken one. */}
                              {filtered ? (
                                <PortalEmptyState
                                  icon={publicIpsHeaderIcon}
                                  title="No public IPv4 addresses match these filters"
                                  description={nextCursor
                                    ? 'Filters apply only to the addresses loaded so far, and more pages are waiting — use “Load more” below before concluding an address is not here.'
                                    : 'Clear a filter to see the whole loaded set.'}
                                  color={IPAM_COLOR}
                                  py={2}
                                />
                              ) : (
                                <PortalEmptyState
                                  icon={publicIpsHeaderIcon}
                                  title={zeroState.title}
                                  description={zeroState.description}
                                  color={IPAM_COLOR}
                                  py={2}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* A REAL cursor button, not a page-size guess.
                      GetIpamDiscoveredPublicAddresses has no SDK paginator, so
                      the first response is not a bounded first page and there is
                      no total to render. */}
                  {nextCursor && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ExpandMoreIcon />}
                        disabled={loadingMore}
                        onClick={() => loadAddresses(nextCursor)}
                        sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
                      >
                        {loadingMore ? 'Loading…' : 'Load more addresses'}
                      </Button>
                    </Box>
                  )}

                  {/* Withheld while the read is failing: "Every page Prism holds has
                      been loaded" is a completeness claim, and a failed read has
                      loaded nothing to be complete about. */}
                  {!tableError && (
                    <Typography
                      sx={{ mt: 1.5, fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}
                    >
                      <b>Counts, filters and the idle-first order describe the {rows.length}{' '}
                      {plural(rows.length, 'address', 'addresses')} loaded so far.</b>{' '}
                      {nextCursor
                        ? 'AWS provides no total for this dataset and Prism walks it a page at a time, so more addresses — including more idle ones — may be waiting behind “Load more”.'
                        : 'Every page Prism holds has been loaded.'}
                      {' '}An allocated public IPv4 address bills for every hour it exists, attached
                      or not. Prism never releases an address; release it in the owning account.
                    </Typography>
                  )}
                </>
              )}
            </Paper>
          </motion.div>
        </>
      )}
    </motion.div>
  );
}
